import type { IconKind } from '../components/Icon/Icon';

export type DayOptionsCheckItem = {
  id: string;
  label: string;
  sublabel?: string;
  defaultChecked?: boolean;
  dot?: string;
  icon?: IconKind;
  iconColor?: string;
};

export type DayStatusAxis = {
  key: string;
  label: string;
  hint: string;
  note?: string;
  items: DayOptionsCheckItem[];
};

export type DayOptionsFlagGroup = {
  label: string;
  labelColor: string;
  items: DayOptionsCheckItem[];
};

export type DayQuickFilter = {
  id: string;
  label: string;
  color: string;
  tint: string;
};

export const DAY_OPERATORIES: DayOptionsCheckItem[] = [
  { id: 'op1', label: 'Op 1', sublabel: 'General' },
  { id: 'op2', label: 'Op 2', sublabel: 'General' },
  { id: 'op3', label: 'Op 3', sublabel: 'Quiet · sedation-friendly' },
  { id: 'op4', label: 'Op 4', sublabel: 'Surgical' },
  { id: 'op5', label: 'Op 5', sublabel: 'Hygiene' },
];

/**
 * Quick filters — one-tap spotlights on "who needs what today".
 * The server evaluates them (`quick` on /api/calendar/day and /next7).
 */
export const DAY_QUICK_FILTERS: DayQuickFilter[] = [
  { id: 'red', label: 'Needs attention', color: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  { id: 'unconfirmed', label: 'Unconfirmed', color: 'var(--gold)', tint: 'var(--gold-tint)' },
  { id: 'notready', label: 'Not ready', color: 'var(--gold)', tint: 'var(--gold-tint)' },
  { id: 'building', label: 'In the building', color: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  { id: 'newpt', label: 'New patients', color: 'var(--sage-0)', tint: 'var(--success-tint)' },
];

/**
 * Appointment status — the three-axis model (ADR-001 / FD70).
 * Booking (on the books?) · Confirmation (reached them?) · Visit (where now?).
 * Replaces the old flat single-status list. Un-ticked values are left out of the request
 * (`booking` / `confirmation` / `visit`), so ids must match the backend enums.
 */
export const DAY_STATUS_AXES: DayStatusAxis[] = [
  {
    key: 'booking',
    label: 'Booking',
    hint: 'on the books?',
    items: [
      { id: 'SCHEDULED', label: 'Scheduled', dot: 'var(--ink-3)', defaultChecked: true },
      { id: 'RESCHEDULED', label: 'Rescheduled', dot: 'var(--ink-3)', defaultChecked: true },
      { id: 'CANCELLED', label: 'Cancelled', dot: 'var(--terracotta)', defaultChecked: true },
      { id: 'BROKEN', label: 'Broken', dot: 'var(--terracotta)', defaultChecked: true },
      { id: 'COMPLETED', label: 'Completed', dot: 'var(--sage-0)', defaultChecked: true },
    ],
  },
  {
    key: 'confirmation',
    label: 'Confirmation',
    hint: 'reached them?',
    items: [
      { id: 'UNCONFIRMED', label: 'Unconfirmed', dot: 'var(--terracotta)', defaultChecked: true },
      { id: 'ATTEMPTED', label: 'Attempted · left message', dot: 'var(--gold)', defaultChecked: true },
      { id: 'CONFIRMED', label: 'Confirmed', dot: 'var(--success)', defaultChecked: true },
    ],
  },
  {
    key: 'visit',
    label: 'Visit',
    hint: 'where now?',
    note: 'Populated by kiosk / ELVA panel',
    items: [
      { id: 'NOT_ARRIVED', label: 'Not arrived', dot: 'var(--ink-3)', defaultChecked: true },
      { id: 'ARRIVED', label: 'Arrived · waiting', dot: 'var(--gold)', defaultChecked: true },
      { id: 'SEATED', label: 'Seated', dot: 'var(--ai-3)', defaultChecked: true },
      { id: 'TREATMENT_DONE', label: 'Treatment done', dot: 'var(--sage-0)', defaultChecked: true },
      { id: 'CHECKED_OUT', label: 'Checked out', dot: 'var(--ink-3)', defaultChecked: true },
      { id: 'NO_SHOW', label: 'No-show', dot: 'var(--terracotta)', defaultChecked: true },
    ],
  },
];

/**
 * Flags — readiness & attention markers only.
 * `unconfirmed` and `confirmed` were removed: they are now the Confirmation axis.
 */
export const DAY_FLAG_GROUPS: DayOptionsFlagGroup[] = [
  {
    label: 'Red · Critical',
    labelColor: 'var(--terracotta)',
    items: [
      { id: 'elig_fail', label: 'Eligibility failed', icon: 'shield', iconColor: 'var(--terracotta)' },
      { id: 'lab_missing', label: 'Lab not arrived', icon: 'flask', iconColor: 'var(--terracotta)' },
      { id: 'med_alert', label: 'Medical alert needs confirmation', icon: 'flame', iconColor: 'var(--terracotta)' },
    ],
  },
  {
    label: 'Yellow · Attention',
    labelColor: 'var(--gold)',
    items: [
      { id: 'forms_inc', label: 'Forms incomplete', icon: 'doc', iconColor: 'var(--gold)' },
      { id: 'balance', label: 'Outstanding balance', icon: 'dollar', iconColor: 'var(--gold)' },
      { id: 'preauth_p', label: 'Pre-auth pending', icon: 'clock', iconColor: 'var(--gold)' },
      { id: 'anxiety', label: 'Anxiety flag', icon: 'heart', iconColor: 'var(--gold)' },
      { id: 'new_pt', label: 'New patient', icon: 'sparkle', iconColor: 'var(--gold)' },
      { id: 'big_case', label: 'Big case · TC coordinator', icon: 'crown', iconColor: 'var(--gold)' },
    ],
  },
  {
    label: 'Green · ELVA handled',
    labelColor: 'var(--success)',
    items: [
      { id: 'elig_ok', label: 'Eligibility verified', icon: 'shield-check', iconColor: 'var(--success)' },
      { id: 'forms_ok', label: 'Forms submitted', icon: 'check', iconColor: 'var(--success)' },
      { id: 'reminded', label: 'Reminder acknowledged', icon: 'check', iconColor: 'var(--success)' },
    ],
  },
  {
    label: 'Context',
    labelColor: 'var(--gold)',
    items: [
      { id: 'birthday', label: 'Birthday today', icon: 'cake', iconColor: 'var(--gold)' },
      { id: 'vip', label: 'VIP / top referrer', icon: 'crown', iconColor: 'var(--gold)' },
    ],
  },
];
